import { useMemo, useRef } from 'react';
import type { AthleteTeamProfile } from '@strength/domain';
import { normalizeZeroRow } from '@strength/zero-schema';
import {
  rowAttributes,
  useHistoryScrollState,
  useZeroVirtualizer,
} from '@rocicorp/zero-virtual/react';
import { useCallback } from 'react';
import { queries } from '@strength/zero-schema';

/** The picker's list context: every filter that, changed, resets the list. */
type AthleteListContextParams = {
  teamIds: string[];
  searchTerm: string;
  parentTrackId: string | undefined;
  athleteIds: string[] | undefined;
  athleteIdsMode: 'in' | 'notIn' | undefined;
};

/** The paging cursor: the root-table sort columns of `roster.athletesPage`. */
type AthleteStartRow = {
  familyName: string;
  givenName: string;
  id: string;
};

/** The raw (pre-normalization) shape of a `roster.athletesPage` row. */
type AthletePageRow = {
  id: string;
  givenName: string;
  familyName: string;
  email: string | null;
  primaryPhone: string | null;
  birthdate: string | null;
  gender: string | null;
  imageUrl: string | null;
  athleteMemberships:
    | {
        teamMembershipAttributes:
          | AthleteTeamProfile['attributes']
          | null
          | undefined;
      }[]
    | null
    | undefined;
};

function rowToAthlete(row: AthletePageRow): AthleteTeamProfile {
  const membership = row.athleteMemberships?.[0];
  return {
    ...row,
    attributes: membership?.teamMembershipAttributes ?? null,
    tracks: [],
    teams: [],
  };
}

/**
 * The zero-virtual rendering of the track form's athlete picker (SC-5674):
 * bidirectional infinite scroll over `roster.athletesPage`, scroll anchoring
 * while pages load, and scroll-state persistence through
 * `useHistoryScrollState`.
 *
 * Rows render in normal document flow; the not-yet-loaded rows above and
 * below are stood in for by spacer elements sized `spaceBefore`/`spaceAfter`
 * and estimate-height placeholders, so the viewport stays stable across
 * paging. The filters (search, parent-track roster, selected/unselected tab)
 * are mirrored into the page query args — `listContextParams` carries them so
 * a filter change resets the list's anchor and scroll position.
 *
 * Dates are normalized with the house read funnel (`normalizeZeroRow`): the
 * virtualizer bypasses `useZeroQuery`, so its rows arrive epoch-ms.
 */
export function ZeroAthletePicker({
  teamIds,
  searchTerm,
  parentTrackId,
  athleteIds,
  athleteIdsMode,
  renderAthlete,
  tabpanelId,
  tabpanelLabelledBy,
  header,
}: {
  teamIds: string[];
  searchTerm: string;
  parentTrackId?: string;
  athleteIds?: string[];
  athleteIdsMode?: 'in' | 'notIn';
  renderAthlete: (athlete: AthleteTeamProfile) => React.ReactNode;
  tabpanelId: string;
  tabpanelLabelledBy: string;
  header?: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, onScrollStateChange] =
    useHistoryScrollState<AthleteStartRow>();

  // Identity change = "new context" = reset anchor + scroll position. The
  // filters live here and in the query args; useMemo keeps the reference
  // stable across renders with unchanged filters.
  const listContextParams = useMemo(
    () => ({
      teamIds,
      searchTerm,
      parentTrackId,
      athleteIds,
      athleteIdsMode,
    }),
    [teamIds, searchTerm, parentTrackId, athleteIds, athleteIdsMode],
  );

  const { items, spaceBefore, spaceAfter } = useZeroVirtualizer<
    AthleteListContextParams,
    AthletePageRow,
    AthleteStartRow
  >({
    listContextParams,
    getScrollElement: useCallback(() => scrollRef.current, []),
    estimateSize: useCallback(() => 56, []),
    getRowKey: (row) => row.id,
    toStartRow: (row) => ({
      familyName: row.familyName,
      givenName: row.givenName,
      id: row.id,
    }),
    getPageQuery: ({ limit, start, dir }) => ({
      // SAFETY: zero-virtual's GetQueryReturnType pins the query input to
      // `any`, whose `args` become optional — failing contravariant
      // assignability with every named query that validates required args.
      // The runtime value is the same QueryRequest and its rows match TRow
      // by construction (dates arrive epoch-ms and are normalized below
      // before any render use).
      query: queries.roster.athletesPage({
        teamIds,
        limit,
        start,
        dir,
        searchTerm: searchTerm || undefined,
        parentTrackId,
        athleteIds,
        athleteIdsMode,
      }) as never,
    }),
    getSingleQuery: ({ id }) => ({
      query: queries.roster.athleteProfileById({ id, teamIds }) as never,
    }),
    scrollState,
    onScrollStateChange,
  });

  return (
    <div
      ref={scrollRef}
      role="tabpanel"
      id={tabpanelId}
      aria-labelledby={tabpanelLabelledBy}
      className="overflow-y-auto border rounded flex-1 min-h-[200px]"
    >
      {header}
      <div>
        <div style={{ height: spaceBefore }} />
        {items.map(({ index, key, row }) => (
          <div key={key} {...rowAttributes(index, key)}>
            {row ? (
              renderAthlete(rowToAthlete(normalizeZeroRow(row, 'Profile')))
            ) : (
              <div style={{ height: 56 }} aria-hidden="true" />
            )}
          </div>
        ))}
        <div style={{ height: spaceAfter }} />
      </div>
    </div>
  );
}
